import { Namespace } from '@36node-fcp/auth-sdk';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { concat } from 'lodash';
import React, { useMemo } from 'react';

import { AdColumnsType, AdTable } from 'src/components/antd/ad-table';
import { ExportModal } from 'src/components/xlsx-export-modal';
import { ROOT_NS } from 'src/config';
import { IllegalHandleResultUiList } from 'src/features/illegal';
import { NamespaceReportItem, useNamespaceReport } from 'src/features/illegal-handle-report';
import { useNamespaceList } from 'src/features/users';
import { getNsPathnameFromString } from 'src/lib/other';
import { useSlice } from 'src/lib/react-slice';

import SearchForm from './illegal-handle-search.form';
import { useReportSearch } from './illegal-handle.context';

type State = {
  exportVisible: boolean;
};

const initState: State = {
  exportVisible: false,
};
const reducers = {
  openExport(state: State) {
    state.exportVisible = true;
  },
  closeExport(state: State) {
    state.exportVisible = false;
  },
};

const otherColumns = [
  {
    title: '违法数',
    dataIndex: 'illegalCount',
  },
  {
    title: '处置完成数',
    dataIndex: 'illegalHandleClosedCount',
  },
  {
    title: IllegalHandleResultUiList[0].text,
    dataIndex: 'illegalHandleResult1Count',
  },
  {
    title: IllegalHandleResultUiList[1].text,
    dataIndex: 'illegalHandleResult2Count',
  },
  {
    title: IllegalHandleResultUiList[2].text,
    dataIndex: 'illegalHandleResult3Count',
  },
  {
    title: IllegalHandleResultUiList[3].text,
    dataIndex: 'illegalHandleResult4Count',
  },
  {
    title: IllegalHandleResultUiList[4].text,
    dataIndex: 'illegalHandleResult5Count',
  },
  {
    title: IllegalHandleResultUiList[5].text,
    dataIndex: 'illegalHandleResult6Count',
  },
  {
    title: IllegalHandleResultUiList[6].text,
    dataIndex: 'illegalHandleResult7Count',
  },
];

/**
 * 部门报表
 */
const NamespaceReport: React.FC = () => {
  const [{ exportVisible }, dispatch] = useSlice(reducers, initState);
  const [, search] = useReportSearch();
  const [{ loading, list, result = [], total }] = useNamespaceReport(search);
  const [{ result: namespaces = [], loading: nsLoading }] = useNamespaceList({ ns_start: ROOT_NS, _limit: 1000 });

  const columns = useMemo<AdColumnsType<NamespaceReportItem>>(() => {
    const nsColumn = {
      title: '部门名',
      dataIndex: 'ns',
      compute: (val: Namespace) => val.name,
    };
    return concat([nsColumn], otherColumns);
  }, [search]);

  const exportColumns = useMemo<AdColumnsType<NamespaceReportItem>>(() => {
    const exportNsColumn = {
      title: '部门名',
      dataIndex: 'ns',
      compute: (val: Namespace) => getNsPathnameFromString(val.ns, namespaces, 1),
    };
    return concat([exportNsColumn], otherColumns);
  }, [search, nsLoading]);

  if (loading || nsLoading) {
    // TODO: 去掉 Loading 会导致表格树状结构无法展开
    return <Spin />;
  }

  return (
    <>
      <AdTable
        bordered
        columns={columns}
        title={<div>按部门（{total}）</div>}
        rowKey="uniqKey"
        loading={loading}
        scroll={{ x: 'max-content' }}
        dataSource={result}
        showColumnsFilter={false}
        pagination={false}
        onExport={dispatch.openExport}
        extraTools={<SearchForm />}
        expandable={{ defaultExpandAllRows: true }}
      />
      {exportVisible && (
        <ExportModal
          dataSource={list}
          columns={exportColumns}
          filename={`违法处置统计-部门报表${dayjs().format('YYYY-MM-DD')}.xlsx`}
          onClose={dispatch.closeExport}
          title="违法处置统计-部门报表"
          total={total}
        />
      )}
    </>
  );
};

export default NamespaceReport;
